/**
 * Employee Mobile API client — Prompt 15B §6 + Prompt 18B2-P5 §4-§12.
 *
 * Dedicated axios instance for the Employee Mobile surface. The
 * canonical MiseOne manager `api` client uses cookie session +
 * automatic /auth/refresh; this client uses a Bearer employee JWT
 * held in localStorage and NEVER touches the manager cookie.
 *
 *   * `withCredentials: false` guarantees the manager cookie is not
 *     accidentally sent on employee routes.
 *   * The request interceptor attaches the employee token from
 *     localStorage on every call.
 *   * Prompt 18B2-P5 §4 · §7 — a longer-lived opaque
 *     `device_session_token` is persisted alongside identity so an
 *     expired short access JWT can be silently renewed via
 *     `POST /workforce/employee/auth/refresh` without prompting the
 *     employee for the PIN or Activation Code. The trusted-device
 *     credential is treated as a bearer secret: stored client-side
 *     only, NEVER embedded in URLs / manifest / query parameters.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Prompt 18B2-P5 §4 · §10 — persist the short-lived employee_access
// JWT in localStorage so closing the browser tab / installed PWA
// does NOT by itself force a fresh Activation Code or PIN on the
// next open. Identity (`company_id` / `employee_id`) persists in
// localStorage as before. The `device_session_token` (opaque,
// server-generated, rotated on every refresh) enables the trusted
// silent-refresh boot flow.
const TOKEN_KEY = "miseone.workforce.employee.token";
const IDENTITY_KEY = "miseone.workforce.employee.identity";
const DEVICE_SESSION_KEY = "miseone.workforce.employee.device_session";

export function getEmployeeToken() {
    try { return localStorage.getItem(TOKEN_KEY) || null; }
    catch { return null; }
}

export function setEmployeeToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch { /* storage disabled — ignore */ }
}

export function getDeviceSession() {
    try {
        const raw = localStorage.getItem(DEVICE_SESSION_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw);
        if (v && typeof v.device_session_id === "string"
            && typeof v.device_session_token === "string") return v;
        return null;
    } catch { return null; }
}

export function setDeviceSession(session) {
    try {
        if (session && session.device_session_id
            && session.device_session_token) {
            localStorage.setItem(DEVICE_SESSION_KEY, JSON.stringify({
                device_session_id: session.device_session_id,
                device_session_token: session.device_session_token,
            }));
        } else {
            localStorage.removeItem(DEVICE_SESSION_KEY);
        }
    } catch { /* storage disabled — ignore */ }
}

export function getEmployeeIdentity() {
    try {
        const raw = localStorage.getItem(IDENTITY_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw);
        if (v && typeof v.company_id === "string"
            && typeof v.employee_id === "string") return v;
        return null;
    } catch { return null; }
}

export function setEmployeeIdentity(identity) {
    try {
        if (identity && identity.company_id && identity.employee_id) {
            localStorage.setItem(IDENTITY_KEY, JSON.stringify({
                company_id: identity.company_id,
                employee_id: identity.employee_id,
            }));
        } else {
            localStorage.removeItem(IDENTITY_KEY);
        }
    } catch { /* storage disabled — ignore */ }
}

export const employeeApi = axios.create({
    baseURL: API_BASE,
    withCredentials: false,
});

employeeApi.interceptors.request.use((config) => {
    const token = getEmployeeToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});
